import { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { logout, getCurrentUser } from '../auth';
import { Brand } from '../components/Layout';

const MENU_ITEMS = [
  {
    to: '/admin/users',
    label: 'Customer Directory',
    icon: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
  },
  {
    to: '/admin/create-user',
    label: 'Record Service Visit',
    icon: 'M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    to: '/',
    label: 'Return to Website',
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getCurrentUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar & scroll to top on route change
  useEffect(() => {
    setSidebarOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  // Close sidebar on Escape key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') setSidebarOpen(false);
  }, []);

  useEffect(() => {
    if (sidebarOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [sidebarOpen, handleKeyDown]);

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen flex bg-[#f4f9f6] text-primary font-body selection:bg-accent-soft selection:text-primary relative overflow-hidden">
      {/* Background Animated Ambient Glow Bulbs */}
      <div className="fixed -top-32 -left-32 w-96 h-96 rounded bg-accent/15 blur-[90px] pointer-events-none z-0 animate-pulse" />
      <div className="fixed top-1/2 right-0 w-[500px] h-[500px] rounded bg-cta/10 blur-[120px] pointer-events-none z-0 animate-pulse" style={{ animationDuration: '6s' }} />

      {/* ─── MOBILE BACKDROP OVERLAY ─── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ─── DARK FOREST EMERALD SIDEBAR ─── */}
      {/* Desktop: fixed left sidebar (w-72). Mobile: slide-out drawer over content. */}
      <aside
        className={`
          fixed top-0 left-0 bottom-0 w-72 bg-gradient-to-b from-primary-dark via-primary to-primary-deep text-white
          flex flex-col justify-between p-6 shadow-2xl border-r border-accent/20 overflow-hidden
          transition-transform duration-300 ease-in-out
          z-50
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:z-40
        `}
      >
        {/* Background Decorative Mesh & Glow */}
        <div className="absolute inset-0 opacity-15 bg-[radial-gradient(var(--color-accent-bright)_1px,transparent_1px)] [background-size:20px_20px] pointer-events-none rounded" />
        <div className="absolute -top-10 -left-10 w-40 h-40 rounded bg-accent-bright/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded bg-cta/20 blur-3xl pointer-events-none" />
        <div className="relative z-10">
          {/* Brand Header */}
          <div className="flex items-center gap-3.5 mb-8 pb-5 border-b border-white/15 group justify-between">
            <Brand light={true} />

            {/* Close button — mobile only */}
            <button
              className="ml-auto w-8 h-8 rounded bg-white/10 border border-white/15 grid place-items-center text-white/70 hover:text-white hover:bg-white/20 transition-colors cursor-pointer md:hidden"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation Items */}
          <nav className="space-y-2">
            {MENU_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3.5 px-4 py-3 rounded font-extrabold text-sm transition-colors no-underline ${isActive
                    ? 'bg-cta text-white shadow-md border border-white/20'
                    : 'text-white/70 bg-white/10 hover:bg-white/20'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={`w-8 h-8 rounded grid place-items-center shrink-0 transition-colors ${isActive
                      ? 'bg-white/25 border border-white/30'
                      : 'bg-white/10 border border-white/15'
                      }`}>
                      <svg className={`w-5 h-5 ${isActive ? 'text-white' : 'text-accent-bright'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                      </svg>
                    </span>
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* Footer User Profile & Logout Pod - Fixed at Bottom */}
        <div className="pt-4 border-t border-white/15 space-y-3 shrink-0 relative z-10">
          <div className="flex items-center justify-between p-3 rounded bg-white/10 border border-white/15 backdrop-blur-md">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded bg-gradient-to-br from-accent-bright to-cta text-white font-black grid place-items-center text-xs shrink-0 shadow-md">
                {user?.email ? user.email[0].toUpperCase() : 'A'}
              </div>
              <div className="min-w-0">
                <span className="block text-xs font-bold text-white truncate">{user?.email || 'admin@ppt.com'}</span>
                <span className="block text-[10px] text-accent-bright font-extrabold uppercase tracking-wider">Super Admin</span>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="w-8 h-8 rounded bg-red-500/20 hover:bg-red-600 text-red-300 hover:text-white border border-red-500/30 grid place-items-center transition-colors cursor-pointer shrink-0 shadow-sm"
              title="Sign Out Portal"
              aria-label="Sign Out Portal"
            >
              <svg className="w-4 h-4 text-current" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </aside>

      {/* ─── MAIN WORKSPACE PANEL ─── */}
      {/* Desktop: offset by sidebar width. Mobile: full width, no offset. */}
      <div className="flex-1 flex flex-col min-w-0 ml-0 md:ml-72 relative z-10">
        {/* Translucent Glass Header Bar */}
        <header className="h-16 shrink-0 flex items-center justify-between px-3.5 sm:px-6 md:px-8 bg-white sticky top-0 z-30 relative header-sweep border-b border-slate-200 shadow-sm">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            {/* Hamburger menu button — mobile only */}
            <button
              className="w-9 h-9 rounded bg-accent-soft text-cta border border-accent/30 flex items-center justify-center hover:bg-accent-soft/80 active:scale-95 transition-all cursor-pointer shadow-2xs md:hidden shrink-0"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar menu"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>

            {/* Mobile Brand / Page Title */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-7 h-7 rounded bg-gradient-to-br from-accent-bright to-cta text-white font-black grid place-items-center text-xs shadow-xs md:hidden shrink-0">
                ✦
              </span>
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-xs font-medium text-txt-muted hidden sm:inline">Dashboard</span>
                <span className="text-txt-muted/40 hidden sm:inline">/</span>
                <h1 className="font-heading text-sm sm:text-base font-bold text-primary m-0 truncate">Customer Directory</h1>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-2 text-xs font-bold text-accent-muted bg-white border border-slate-200 px-3 py-1.5 rounded shadow-2xs">
              <span className="w-2 h-2 rounded bg-cta shrink-0 animate-pulse" />
              <span className="hidden sm:inline font-semibold text-slate-700">{user?.email || 'admin@ppt.com'}</span>
              <span className="sm:hidden font-extrabold text-cta">{user?.email ? user.email[0].toUpperCase() : 'A'}</span>
            </div>
          </div>
        </header>

        {/* Page Workspace Content */}
        <main className="p-3 sm:p-5 md:p-6 flex-1 w-full space-y-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
